/**
 * Provider marks — brand colours + drawn monograms (REDESIGN §7a).
 *
 * These are OUR OWN simplified marks: a rounded square in a colour chosen to evoke the
 * institution, carrying a monogram we drew as SVG path data. No institution's logo file is
 * copied, referenced or fetched, and a mark is never presented as an official logo. The
 * monogram — not the colour — is what identifies the provider; colours only have to be
 * distinguishable enough and legible on both grounds.
 *
 * Pure data (no React, no DOM) so `tsc -p shared/tsconfig.json` and `node --test` can both
 * read it. `ProviderMark.tsx` renders it.
 *
 * Contrast contract, asserted in `test/providers.test.ts`: every light colour is ≥3:1 against
 * every light ground of both apps AND against white ink; every dark colour is ≥3:1 against
 * every dark ground AND against the dark ink. 3:1 is the WCAG 2.2 threshold for non-text
 * graphical objects (1.4.11).
 */

/** The providers we draw a mark for. `other` is the neutral fallback. */
export const PROVIDER_MARK_IDS = [
  'discount',
  'max',
  'cal',
  'isracard',
  'leumi',
  'hapoalim',
  'mizrahi',
  'beinleumi',
  'one-zero',
  'pepper',
  'amex',
  'other',
] as const;

export type ProviderMarkId = (typeof PROVIDER_MARK_IDS)[number];

/** The three sizes of §7a. */
export const PROVIDER_MARK_SIZES = [20, 28, 40] as const;
export type ProviderMarkSize = (typeof PROVIDER_MARK_SIZES)[number];

/** One provider's colours. `light`/`dark` are the two theme values of `--fk-brand-<id>`. */
export interface ProviderColor {
  light: string;
  dark: string;
}

/**
 * Brand colours per theme. Mirrored into `--fk-brand-*` in `kit.css`; `test/providers.test.ts`
 * parses that file and fails if the two ever drift apart.
 */
export const PROVIDER_COLORS: Readonly<Record<ProviderMarkId, ProviderColor>> = {
  discount: { light: '#0F7B5F', dark: '#2FA383' },
  max: { light: '#1E293B', dark: '#CBD5E1' },
  cal: { light: '#1E4FA3', dark: '#6A9BE8' },
  isracard: { light: '#0E6E8C', dark: '#4FB6D6' },
  leumi: { light: '#3C3F91', dark: '#9296EA' },
  hapoalim: { light: '#B02430', dark: '#F08B93' },
  mizrahi: { light: '#9A4A08', dark: '#E09A55' },
  beinleumi: { light: '#0B5C6B', dark: '#57C2CE' },
  'one-zero': { light: '#4E6B14', dark: '#A9CB55' },
  pepper: { light: '#7A2A8C', dark: '#DA92EC' },
  amex: { light: '#12599E', dark: '#6FB4EA' },
  other: { light: '#5A6B82', dark: '#9AA8BE' },
};

/**
 * The monogram we draw inside the square, as SVG path data in a 24×24 box, stroked (never
 * filled) so one stroke width serves all three sizes. Hand-drawn letterforms — no font is
 * involved, so the mark looks the same whatever the reader has installed.
 */
export const PROVIDER_MONOGRAMS: Readonly<Record<ProviderMarkId, string>> = {
  // D — דיסקונט
  discount: 'M9 6.5h2.4a5.5 5.5 0 0 1 0 11H9z',
  // M, angular — Max
  max: 'M8 17.5v-11l4 5.5 4-5.5v11',
  // C — Cal
  cal: 'M16.2 8.4a5 5 0 1 0 0 7.2',
  // I with serifs — ישראכרט
  isracard: 'M12 6.5v11M9.2 6.5h5.6M9.2 17.5h5.6',
  // L — לאומי
  leumi: 'M9.5 6.5v11h5.5',
  // H — הפועלים
  hapoalim: 'M9 6.5v11M15 6.5v11M9 12h6',
  // lowercase m, rounded arches — מזרחי (distinct from Max's angular M)
  mizrahi: 'M8 17.5v-7M8 12.6a2 2 0 0 1 4 0v4.9M12 12.6a2 2 0 0 1 4 0v4.9',
  // B — הבינלאומי
  beinleumi: 'M9.5 6.5h3a2.75 2.75 0 0 1 0 5.5h-3zM9.5 12h3.4a2.75 2.75 0 0 1 0 5.5H9.5z',
  // 1 0 — One Zero
  'one-zero': 'M7.4 8.3l1.7-1.8v11M17.2 12a2.6 4.6 0 1 1-5.2 0a2.6 4.6 0 1 1 5.2 0',
  // P — Pepper
  pepper: 'M9.5 17.5v-11h3.1a3 3 0 0 1 0 6H9.5',
  // A — American Express
  amex: 'M7.8 17.5l4.2-11 4.2 11M9.7 13.8h4.6',
  // neutral: a generic institution (columns), never a letter
  other: 'M6 10.6l6-4 6 4M7.6 11.4v5.6M12 11.4v5.6M16.4 11.4v5.6M5.6 17.5h12.8',
};

/** Every id we draw, as a set, for `isProviderMarkId`. */
const MARK_IDS = new Set<string>(PROVIDER_MARK_IDS);

/** True when we have a drawn mark for this Financy `providerId`. */
export function isProviderMarkId(id: string | null | undefined): id is ProviderMarkId {
  return typeof id === 'string' && MARK_IDS.has(id);
}

/**
 * Resolve any Financy `providerId` (including `manual`, a future provider, or null) to the id
 * of a mark we can draw. Unknown providers get the neutral one — never a blank.
 */
export function providerMarkId(id: string | null | undefined): ProviderMarkId {
  return isProviderMarkId(id) ? id : 'other';
}

/** The CSS custom property holding a provider's brand colour in the active theme. */
export function providerColorVar(id: ProviderMarkId): string {
  return `--fk-brand-${id}`;
}
